/**
 * Billing System API Service
 * Handles all billing operations including pricing, balances, and transactions
 */

import { apiClient } from './apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface PricingConfiguration {
  id: number;
  name: string;
  setup_fee: string;
  guest_base_fee: string;
  companion_fee: string;
  image_message_fee: string;
  save_the_date_fee: string;
  usher_service_fee: string;
  is_active: boolean;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  notes?: string;
}

export interface HostBalance {
  id: number;
  host: string;
  host_name: string;
  host_phone: string;
  host_email: string;
  balance: string;
  is_negative: boolean;
  is_zero: boolean;
  is_positive: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminHostBalance extends HostBalance {
  is_mirsal_member: boolean;
  event_count: number;
  transaction_count: number;
  last_transaction_date: string | null;
}

export interface AdminBalanceSummary {
  total_hosts: number;
  total_balance: string;
  negative_balance_count: number;
  zero_balance_count: number;
  total_events: number;
}

export interface AdminBalanceListParams {
  filter_balance_status?: 'all' | 'positive' | 'negative' | 'zero';
  filter_mirsal_member?: 'all' | 'true' | 'false';
  min_balance?: string;
  max_balance?: string;
  search?: string;
  sort_by?: 'balance' | 'host_name' | 'event_count' | 'last_transaction_date';
  sort_order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export interface AdminBalanceListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AdminHostBalance[];
  summary: AdminBalanceSummary;
}

export interface AdminBalanceDetailResponse {
  balance: AdminHostBalance;
  events: EventWithTransactions[];
  recent_activities: BillingTransaction[];
}

export interface BillingTransaction {
  id: number;
  host: string;
  host_name: string;
  transaction_type: 'charge' | 'refund' | 'topup' | 'setup_fee' | 'guest_base_fee' | 'companion_fee' | 'image_message_fee' | 'save_the_date_fee' | 'extra_service' | 'usher_service_fee';
  amount: string;
  description: string;
  related_event?: string;
  event_title?: string;
  related_guest_event?: string;
  guest_name?: string;
  service_name?: string;
  balance_before: string;
  balance_after: string;
  is_charge: boolean;
  is_refund: boolean;
  is_topup: boolean;
  is_empty_companion?: boolean;
  metadata?: Record<string, any>;
  created_by?: string;
  created_at: string;
}

export interface PricingConfigurationCreateData {
  name: string;
  setup_fee: string;
  guest_base_fee: string;
  companion_fee: string;
  image_message_fee: string;
  save_the_date_fee: string;
  usher_service_fee: string;
  is_active?: boolean;
  notes?: string;
}

export interface TopUpData {
  amount: string;
  description?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    type: string;
    message: string;
    details?: any;
  };
}

export interface PaginatedResponse<T> {
  success: boolean;
  results: T[];
  count: number;
  next?: string;
  previous?: string;
}

// ============================================================================
// BILLING SERVICE
// ============================================================================

class BillingService {
  private pricingBaseUrl = '/billing/pricing/';
  private balanceBaseUrl = '/billing/balances/';
  private transactionBaseUrl = '/billing/transactions/';

  // ============================================================================
  // PRICING CONFIGURATION METHODS
  // ============================================================================

  /**
   * Get all pricing configurations
   */
  async getPricingConfigurations(params?: { 
    page?: number; 
    page_size?: number;
    is_active?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<PricingConfiguration>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search) queryParams.append('search', params.search);

    const url = queryParams.toString() 
      ? `${this.pricingBaseUrl}?${queryParams.toString()}`
      : this.pricingBaseUrl;

    const response = await apiClient.get<PaginatedResponse<PricingConfiguration>>(
      url,
      { cache: false }
    );
    return response.data;
  }

  /**
   * Get current active pricing configuration
   * Returns null if no active pricing is found (instead of throwing)
   */
  async getCurrentPricing(): Promise<PricingConfiguration | null> {
    try {
      const response = await apiClient.get<ApiResponse<PricingConfiguration>>(
        `${this.pricingBaseUrl}current/`,
        { cache: true, ttl: 5 * 60 * 1000 } // Cache for 5 minutes
      );
      if (!response.data.success || !response.data.data) {
        return null;
      }
      return response.data.data;
    } catch (error: any) {
      // Handle 404 as "no active pricing" rather than an error
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get a specific pricing configuration by ID
   */
  async getPricingConfiguration(id: number): Promise<PricingConfiguration> {
    const response = await apiClient.get<ApiResponse<PricingConfiguration>>(
      `${this.pricingBaseUrl}${id}/`
    );
    return response.data.data || response.data as any;
  }

  /**
   * Create a new pricing configuration
   */
  async createPricingConfiguration(data: PricingConfigurationCreateData): Promise<ApiResponse<PricingConfiguration>> {
    const response = await apiClient.post<ApiResponse<PricingConfiguration>>(
      this.pricingBaseUrl,
      data
    );
    return response.data;
  }

  /**
   * Update a pricing configuration
   */
  async updatePricingConfiguration(id: number, data: Partial<PricingConfigurationCreateData>): Promise<ApiResponse<PricingConfiguration>> {
    const response = await apiClient.patch<ApiResponse<PricingConfiguration>>(
      `${this.pricingBaseUrl}${id}/`,
      data
    );
    return response.data;
  }

  /**
   * Deactivate a pricing configuration
   */
  async deactivatePricingConfiguration(id: number): Promise<ApiResponse<PricingConfiguration>> {
    const response = await apiClient.post<ApiResponse<PricingConfiguration>>(
      `${this.pricingBaseUrl}${id}/deactivate/`
    );
    return response.data;
  }

  // ============================================================================
  // HOST BALANCE METHODS
  // ============================================================================

  /**
   * Get all host balances (admin only)
   */
  async getHostBalances(): Promise<HostBalance[]> {
    const response = await apiClient.get<PaginatedResponse<HostBalance>>(
      this.balanceBaseUrl,
      { cache: false }
    );
    return response.data.results || [];
  }

  /**
   * Get current user's balance (host)
   */
  async getMyBalance(): Promise<HostBalance> {
    const response = await apiClient.get<ApiResponse<HostBalance>>(
      `${this.balanceBaseUrl}my-balance/`,
      { cache: false }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch balance');
    }
    return response.data.data;
  }

  /**
   * Get a specific host balance by ID
   */
  async getHostBalance(id: number): Promise<HostBalance> {
    const response = await apiClient.get<ApiResponse<HostBalance>>(
      `${this.balanceBaseUrl}${id}/`
    );
    return response.data.data || response.data as any;
  }

  /**
   * Top up a host's balance (admin only)
   */
  async topUpBalance(balanceId: number, data: TopUpData): Promise<ApiResponse<{ balance: HostBalance; transaction: BillingTransaction }>> {
    const response = await apiClient.post<ApiResponse<{ balance: HostBalance; transaction: BillingTransaction }>>(
      `${this.balanceBaseUrl}${balanceId}/top-up/`,
      data
    );
    return response.data;
  }

  // ============================================================================
  // TRANSACTION METHODS
  // ============================================================================

  /**
   * Get all transactions (admin) or user's transactions (host)
   */
  async getTransactions(params?: { host_id?: string; page?: number }): Promise<PaginatedResponse<BillingTransaction>> {
    const queryParams = new URLSearchParams();
    if (params?.host_id) queryParams.append('host_id', params.host_id);
    if (params?.page) queryParams.append('page', params.page.toString());

    const url = queryParams.toString() 
      ? `${this.transactionBaseUrl}?${queryParams.toString()}`
      : this.transactionBaseUrl;

    const response = await apiClient.get<PaginatedResponse<BillingTransaction>>(
      url,
      { cache: false }
    );
    return response.data;
  }

  /**
   * Get current user's transactions (host)
   */
  async getMyTransactions(): Promise<BillingTransaction[]> {
    const response = await apiClient.get<ApiResponse<BillingTransaction[]>>(
      `${this.transactionBaseUrl}my-transactions/`,
      { cache: false }
    );
    if (!response.data.success || !response.data.data) {
      return [];
    }
    return response.data.data;
  }

  /**
   * Get a specific transaction by ID
   */
  async getTransaction(id: number): Promise<BillingTransaction> {
    const response = await apiClient.get<ApiResponse<BillingTransaction>>(
      `${this.transactionBaseUrl}${id}/`
    );
    return response.data.data || response.data as any;
  }

  /**
   * Get events with grouped transactions (host only)
   */
  async getEventsWithTransactions(page: number = 1, pageSize: number = 20): Promise<{
    events: EventWithTransactions[];
    recent_activities: BillingTransaction[];
    pagination?: {
      page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
      next?: string | null;
      previous?: string | null;
    };
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('page_size', pageSize.toString());
    
    const response = await apiClient.get<ApiResponse<{
      events: EventWithTransactions[];
      recent_activities: BillingTransaction[];
      pagination?: {
        page: number;
        page_size: number;
        total_count: number;
        total_pages: number;
        has_next: boolean;
        has_previous: boolean;
        next?: string | null;
        previous?: string | null;
      };
    }>>(
      `${this.balanceBaseUrl}events-with-transactions/?${queryParams.toString()}`,
      { cache: false }
    );
    if (!response.data.success || !response.data.data) {
      return { events: [], recent_activities: [] };
    }
    return response.data.data;
  }

  // ============================================================================
  // ADMIN BALANCE METHODS
  // ============================================================================

  /**
   * Get paginated list of host balances with statistics (admin only)
   */
  async getAdminHostBalances(params?: AdminBalanceListParams): Promise<AdminBalanceListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.filter_balance_status && params.filter_balance_status !== 'all') {
      queryParams.append('filter_balance_status', params.filter_balance_status);
    }
    if (params?.filter_mirsal_member && params.filter_mirsal_member !== 'all') {
      queryParams.append('filter_mirsal_member', params.filter_mirsal_member);
    }
    if (params?.min_balance) {
      queryParams.append('min_balance', params.min_balance);
    }
    if (params?.max_balance) {
      queryParams.append('max_balance', params.max_balance);
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.sort_by) {
      queryParams.append('sort_by', params.sort_by);
    }
    if (params?.sort_order) {
      queryParams.append('sort_order', params.sort_order);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.page_size) {
      queryParams.append('page_size', params.page_size.toString());
    }

    const url = queryParams.toString()
      ? `${this.balanceBaseUrl}admin-list/?${queryParams.toString()}`
      : `${this.balanceBaseUrl}admin-list/`;

    const response = await apiClient.get<AdminBalanceListResponse>(
      url,
      { cache: false }
    );
    return response.data;
  }

  /**
   * Get detailed balance information for a specific host (admin only)
   */
  async getHostAdminDetail(hostBalanceId: number): Promise<AdminBalanceDetailResponse> {
    const response = await apiClient.get<ApiResponse<AdminBalanceDetailResponse>>(
      `${this.balanceBaseUrl}${hostBalanceId}/admin-detail/`,
      { cache: false }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch host detail');
    }
    return response.data.data;
  }

  // Extra Service methods
  async addExtraService(data: ExtraServiceCreateData): Promise<ApiResponse<BillingTransaction>> {
    const response = await apiClient.post<ApiResponse<BillingTransaction>>(
      `${this.transactionBaseUrl}add-extra-service/`,
      data
    );
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to add extra service');
    }
    return response.data;
  }

  async updateExtraService(transactionId: number, data: ExtraServiceUpdateData): Promise<ApiResponse<BillingTransaction>> {
    const response = await apiClient.put<ApiResponse<BillingTransaction>>(
      `${this.transactionBaseUrl}${transactionId}/`,
      data
    );
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to update extra service');
    }
    return response.data;
  }

  async deleteExtraService(transactionId: number): Promise<ApiResponse<BillingTransaction>> {
    const response = await apiClient.delete<ApiResponse<BillingTransaction>>(
      `${this.transactionBaseUrl}${transactionId}/`
    );
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete extra service');
    }
    return response.data;
  }
}

// Extra Service interfaces
export interface ExtraServiceCreateData {
  event_id: string;
  service_name: string;
  amount: string; // Decimal as string
}

export interface ExtraServiceUpdateData {
  service_name: string;
  amount: string; // Decimal as string
}

// Extended Event type with transactions
export interface EventWithTransactions {
  id: string;
  title: string;
  event_date: string;
  event_time: string;
  location: string;
  status: string;
  created_at: string;
  transactions: BillingTransaction[];
  transaction_count: number;
  total_deductions: string;
  series_id?: string;
  series_name?: string;
  is_main_event_in_series?: boolean;
  is_main_event?: boolean;
}

// Export singleton instance
export const billingService = new BillingService();
export default billingService;

